import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { currentBranding } from '../../../config/branding';
import { RoleListInterFace } from '../../../interface/role-management.interface';
import { UserInterFace } from '../../../interface/user-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchFacilityTagsList } from '../../../redux/slice/FacilityTagSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { fetchAllRolesWithOutPage } from '../../../redux/slice/RoleManagementSlice';
import { fetchAllUsers } from '../../../redux/slice/UserManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import {
  emailValidation,
  stringValidation,
  passwordValidation,
  phoneNumberValidation,
} from '../../../shared-functions/Validation';
import userApi from '../../../utils/api/users';

interface Option {
  label: string;
  value: string;
}

interface UserDetailsInterface {
  role: string;
  name: string;
  email: string;
  confirmPassword: string;
  password: string;
  phoneNumber: string;
  countryCode: string;
  facilities?: string[];
}
interface AddUserProperties {
  handleCancel: () => void;
  currentData?: UserInterFace;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add User function component
 */
function AddUser(properties: AddUserProperties) {
  const { t } = useTranslation('userManagement');
  const { handleCancel } = properties;
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.users);
  const { facilityTagsList } = useAppSelector((state) => state.facilityTags);
  const facilityTagsListOption: Option[] = facilityTagsList.map((facilityTag) => {
    return {
      label: facilityTag.facilityTagName,
      value: facilityTag._id,
    };
  });

  const { roleList, loading } = useAppSelector((state) => state.roles);

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const [userDetails, setUserDetails] = useState<UserDetailsInterface>({
    role: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    countryCode: currentBranding === 'energic' ? '966' : '1',
    facilities: [],
  });

  /**
   * Handle the fetch data function
   */
  const fetchData = () => {
    if (!loading) {
      dispatch(fetchAllRolesWithOutPage());
    }
  };

  /**
   * Handle the station detail change
   */
  const handleUserDetailChange = (name: string, value: string | number | (string | number)[]) => {
    setUserDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the form submit function
   */
  const submit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      !stringValidation(1, 100, userDetails.name) ||
      userDetails.role === '' ||
      !emailValidation(userDetails.email) ||
      !phoneNumberValidation(userDetails.phoneNumber) ||
      !passwordValidation(userDetails.password) ||
      userDetails.confirmPassword !== userDetails.password
    ) {
      return;
    }

    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: true,
    });
    const request = {
      name: userDetails.name,
      role: userDetails.role,
      email: userDetails.email,
      password: userDetails.password,
      phoneNumber: userDetails.phoneNumber,
      facilities: userDetails.facilities,
      countryCode: userDetails.countryCode,
    };

    userApi
      .createUser(request)
      .then(() => {
        dispatch(fetchAllUsers({ page, limit }));
        setFormStatus({
          showFormErrors: false,
          hint: false,
          buttonLoader: false,
        });
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          content: (
            <SuccessNotification
              title={t('userCreatedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCancel}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setFormStatus({
          showFormErrors: false,
          hint: false,
          buttonLoader: false,
        });
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  useEffect(() => {
    if (roleList.length === 0) {
      fetchData();
    }
    dispatch(fetchFacilityTagsList());
  }, []);

  return (
    <div className="add-user-side-panel">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />

      <p className="title">{t('addUser')}</p>

      <div className="add-user-scroll-container">
        <div className="add-user-group">
          <div className="profile-card">
            <div className="edit-fields-container">
              {/* ROLE + NAME */}
              <div className="info-row-input">
                <SelectFiled
                  inputLabel={t('role')}
                  name="role"
                  placeholder={t('selectRole')}
                  selectedValue={userDetails.role}
                  onChangeValue={handleUserDetailChange}
                  options={roleList.map((role: RoleListInterFace) => ({
                    label: role.name,
                    value: role._id,
                  }))}
                  valueRequired
                  error={formStatus.showFormErrors && userDetails.role === ''}
                  errorMessage={t('userRoleRequired')}
                />

                <CustomInput
                  inputLabel={t('name')}
                  type="text"
                  placeholder={t('enterName')}
                  name="name"
                  value={userDetails.name}
                  onChangeValue={handleUserDetailChange}
                  valueRequired
                  error={formStatus.showFormErrors && !stringValidation(1, 100, userDetails.name)}
                  errorMessage={t('userNameRequired')}
                />
              </div>

              {/* EMAIL + PHONE */}
              <div className="info-row-input">
                <CustomInput
                  inputLabel={t('email')}
                  type="text"
                  placeholder={t('enterEmail')}
                  name="email"
                  value={userDetails.email}
                  onChangeValue={handleUserDetailChange}
                  valueRequired
                  error={formStatus.showFormErrors && !emailValidation(userDetails.email)}
                  errorMessage={t('invalidEmail')}
                />

                <CustomPhoneNumber
                  inputLabel={t('phoneNumber')}
                  placeholder={t('enterPhoneNumber')}
                  name="phoneNumber"
                  value={userDetails.phoneNumber}
                  countryCode={userDetails.countryCode}
                  onChangeValue={handleUserDetailChange}
                  valueRequired
                  error={
                    formStatus.showFormErrors && !phoneNumberValidation(userDetails.phoneNumber)
                  }
                  errorMessage={t('invalidPhoneNumber')}
                />
              </div>

              {/* PASSWORD + CONFIRM PASSWORD */}
              <div className="info-row-input">
                <CustomInput
                  inputLabel={t('setPassword')}
                  type="password"
                  placeholder={t('enterPassword')}
                  name="password"
                  value={userDetails.password}
                  onChangeValue={handleUserDetailChange}
                  valueRequired
                  error={formStatus.showFormErrors && !passwordValidation(userDetails.password)}
                  errorMessage={t('passwordError')}
                />

                <CustomInput
                  inputLabel={t('reEnterPassword')}
                  type="password"
                  placeholder={t('enterPassword')}
                  name="confirmPassword"
                  value={userDetails.confirmPassword}
                  onChangeValue={handleUserDetailChange}
                  valueRequired
                  error={
                    formStatus.showFormErrors &&
                    (userDetails.password !== userDetails.confirmPassword ||
                      userDetails.confirmPassword === '')
                  }
                  errorMessage={t('passwordMismatch')}
                />
              </div>

              {/* FACILITIES FULL WIDTH */}
              <div>
                <MultiSelectDropdown
                  placeholderFilled={t('facility')}
                  title={t('selectFacility')}
                  options={facilityTagsListOption}
                  name="facilities"
                  placeholder={t('selectFacility')}
                  search
                  selectedChips
                  selectedValues={userDetails.facilities || []}
                  onSelectionChange={handleUserDetailChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="footer-actions">
          <MyButton label={t('cancel')} buttonType="outline" onClickFunc={handleCancel} />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={submit}
            disableButton={formStatus.buttonLoader}
            loading={formStatus.buttonLoader}
          />
        </div>
      </div>
    </div>
  );
}

export default AddUser;
